// src/utils/buildHar.js
// Build a HAR from viewer rows and embed just enough Inspectra metadata
// so validations (DV/Mobian/Context) still work when a HAR is reopened.

// Row timestamps arrive as epoch numbers (live) or ISO strings (proxy / restored
// sessions); older rows carry startedAtMs / timestamp. Always a finite epoch ms.
function rowStartMs(r) {
    const v = r.startedAt ?? r.startedAtMs ?? r.timestamp;
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    const n = typeof v === 'string' ? Date.parse(v) : NaN;
    return Number.isFinite(n) ? n : Date.now();
}

// Rows carry already-decoded bodies, but the original response headers may still
// advertise content-encoding (gzip/br/zstd). Leaving it in would mislead any tool
// that re-decodes an already-decoded body, so the exported copy drops it.
const DECODED_COMMENT = 'body decoded by fx-validator proxy';

function utf8Len(t) {
    try { return new TextEncoder().encode(t).length; } catch { return t.length; }
}

export function buildHarFromRequests(rows = [], opts = {}) {
    const startedDateTime = new Date().toISOString();
    const globalConfig = opts.globalConfig || {};

    const log = {
        version: '1.2',
        creator: { name: 'Inspectra', version: '1.0' },
        browser: { name: 'Inspectra Viewer', version: '1.0' },
        pages: [
            {
                startedDateTime,
                id: 'page_1',
                title: opts.title || 'Live session',
                pageTimings: {}
            }
        ],
        // keep device/context flags at the log level as a fallback
        _inspectra: { globalConfig },
        entries: []
    };

    for (const r of rows) {
        const url = r.url || '';
        const method = r.method || 'GET';

        const reqHeaders = [];
        for (const [k, v] of Object.entries(r.requestHeaders || {})) {
            reqHeaders.push({ name: String(k), value: String(v) });
        }
        const resHeaders = [];
        for (const [k, v] of Object.entries(r.responseHeaders || {})) {
            if (String(k).toLowerCase() === 'content-encoding') continue;
            resHeaders.push({ name: String(k), value: String(v) });
        }

        // Request body → HAR postData
        let postData = undefined;
        if (r.requestBody != null && r.requestBody !== '') {
            const bodyStr = typeof r.requestBody === 'string'
                ? r.requestBody
                : JSON.stringify(r.requestBody);
            postData = {
                mimeType: (r.requestHeaders && (r.requestHeaders['content-type'] || r.requestHeaders['Content-Type'])) || 'application/json',
                text: bodyStr.startsWith('__BINARY__:')
                    ? bodyStr.replace('__BINARY__:', '').split(',')[1] || ''
                    : bodyStr,
                encoding: bodyStr.startsWith('__BINARY__:') ? 'base64' : undefined
            };
        }

        // Response content
        let content = { size: r.size || 0, mimeType: r.responseContentType || '' };
        if (r.responseBody != null && r.responseBody !== '') {
            if (typeof r.responseBody === 'string' && r.responseBody.startsWith('__BINARY__:')) {
                const [meta, b64] = r.responseBody.replace('__BINARY__:', '').split(',');
                const mime = (meta || 'application/octet-stream').split(';')[0];
                content = { ...content, text: b64 || '', encoding: 'base64', mimeType: mime };
            } else {
                const txt = typeof r.responseBody === 'string'
                    ? r.responseBody
                    : JSON.stringify(r.responseBody);
                content = { ...content, text: txt, size: utf8Len(txt), comment: DECODED_COMMENT };
            }
        }

        const entry = {
            pageref: 'page_1',
            startedDateTime: new Date(rowStartMs(r)).toISOString(),
            time: Number.isFinite(r.durationMs) ? r.durationMs : 0,
            request: {
                method,
                url,
                httpVersion: 'HTTP/1.1',
                headers: reqHeaders,
                queryString: (() => {
                    try {
                        const u = new URL(url);
                        return [...u.searchParams.entries()].map(([name, value]) => ({ name, value }));
                    } catch { return []; }
                })(),
                cookies: r.requestCookies || [],
                headersSize: -1,
                bodySize: postData ? (postData.text?.length || 0) : -1,
                postData
            },
            response: {
                status: r.status ?? r.responseCode ?? 0,
                statusText: r.statusText || '',
                httpVersion: 'HTTP/1.1',
                headers: resHeaders,
                cookies: r.responseCookies || [],
                content,
                redirectURL: '',
                headersSize: -1,
                bodySize: content.size || r.size || 0
            },
            cache: {},
            timings: { send: 0, wait: r.durationMs || 0, receive: 0 },
            serverIPAddress: '',
            connection: '',
            // 🔴 Embedded Inspectra metadata to keep validations functional from HAR
            _inspectra: {
                // The row identity evidence HARs are sliced by
                // (utils/evidenceHar.js) — the same id the Checks engine grades.
                requestId: r.requestId || undefined,
                requestType: r.requestType || '',
                validData: r.validData || null,      // includes dvCheck/mobianCheck/expanded/meta
                contextRef: r.contextRef || null,    // latest cached context record
                configAttributes: r.configAttributes || globalConfig || {},
                issues: Array.isArray(r.issues) ? r.issues.slice(0, 50) : [],
                winner: r.winner || null,
                step: r.step || undefined,     // app-flow step marker (per-tab attribution)
                iuRewrite: r.iuRewrite || undefined, // IU Rewrite changes applied before the request left the proxy
                // iu-finder-spec Contract E — USB/logcat rows are request-only and
                // may have a truncated URL; without these a saved capture reloads
                // looking like full MITM traffic ("no response" -> "no fill").
                captureSource: r.captureSource || undefined,
                truncated: r.truncated || undefined,
                // har-corpus-spec WP0 — device/platform provenance, so a
                // re-imported HAR can recover platform without re-parsing UA
                // headers (proxy stamps these on every row; see
                // proxyOnRequestHandler.js classifyPlatform/enrichDeviceInfo).
                platformClass: r.platformClass || undefined,   // e.g. "iOS Mobile"
                devicePlatform: r.devicePlatform || undefined, // e.g. "iOS 18"
                deviceLabel: r.deviceLabel || undefined
            }
        };

        log.entries.push(entry);
    }

    return { log };
}
